import { Button, Callout, Card, ErrorBanner } from "@bruntworkwear-apps/ui";
import { BbWcRates } from "./components/BbWcRates.view";
import { RateBandsView } from "./components/RateBands.view";
import { ReceiptDropzones } from "./components/ReceiptDropzones.view";
import { RunSettings } from "./components/RunSettings.view";
import { SourceToggle } from "./components/SourceToggle.view";
import styles from "./UploadForm.module.css";
import type { UploadFormViewProps } from "./UploadForm.types";

/** Markup for {@link UploadForm}. Presentation only. */
export function UploadFormView(props: UploadFormViewProps) {
  const { defaults, isRunning, error, isSessionExpired, onReauthenticate } = props;

  if (isSessionExpired) {
    return (
      <Card title="Session">
        <Callout tone="warning">
          You are not signed in. Continue to sign in with your BRUNT account.
        </Callout>
        <div className={styles.actions}>
          <Button onClick={onReauthenticate}>Sign in again</Button>
        </div>
      </Card>
    );
  }

  return (
    <div className={styles.form}>
      <SourceToggle
        config={props.receiptSource}
        value={props.wcSource}
        disabled={isRunning}
        onChange={props.onWcSource}
      />

      <ReceiptDropzones
        wcSource={props.wcSource}
        wcReport={props.wcReport}
        opsMaster={props.opsMaster}
        maxUploadMb={defaults.max_upload_mb}
        disabled={isRunning}
        onWcReport={props.onWcReport}
        onClearWcReport={props.onClearWcReport}
        onOpsMaster={props.onOpsMaster}
        onClearOpsMaster={props.onClearOpsMaster}
      />

      <RunSettings
        landingRate={props.landingRate}
        rateError={props.rateError}
        lookbackDays={props.lookbackDays}
        lookbackError={props.lookbackError}
        disabled={isRunning}
        onLandingRate={props.onLandingRate}
        onLookbackDays={props.onLookbackDays}
      />

      <Card title="Landing rate by date">
        <RateBandsView bands={props.bands} flatRate={props.landingRate} disabled={isRunning} />
      </Card>

      <BbWcRates
        rateFallback={props.bbWcRateFallback}
        rateAlways={props.bbWcRateAlways}
        landingRate={props.bbWcLandingRate}
        landingRateError={props.bbWcLandingRateError}
        rateBands={props.bbWcBands}
        customsFallback={props.bbWcCustomsFallback}
        customsAlways={props.bbWcCustomsAlways}
        customsRate={props.bbWcCustomsRate}
        customsRateError={props.bbWcCustomsRateError}
        disabled={isRunning}
        onRateFallback={props.onBbWcRateFallback}
        onRateAlways={props.onBbWcRateAlways}
        onLandingRate={props.onBbWcLandingRate}
        onCustomsFallback={props.onBbWcCustomsFallback}
        onCustomsAlways={props.onBbWcCustomsAlways}
        onCustomsRate={props.onBbWcCustomsRate}
      />

      <ErrorBanner message={error} />

      <div className={styles.actions}>
        <Button onClick={props.onRun} disabled={!props.canSubmit} loading={isRunning}>
          {isRunning ? "Reconciling…" : "Run reconciliation"}
        </Button>
        {isRunning && (
          <span className={styles.running}>
            {props.wcSource === "netsuite"
              ? "Reading receipt lines from NetSuite, then parsing the tracker. The feed can take up to a minute."
              : "Parsing both workbooks. This usually takes a few seconds."}
          </span>
        )}
      </div>
    </div>
  );
}
